import React, { useState, useEffect } from 'react';
import axios from 'axios';

// Hook personnalisé pour la gestion des données (SOLID - Single Responsibility)
const useRoomData = (roomId, initialRoom) => {
  const [roomDetails, setRoomDetails] = useState(null);
  const [charges, setCharges] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const loadRoomDetailsWithFallback = async () => {
    try {
      setLoading(true);
      setError(null);

      // Essayer d'abord la nouvelle endpoint enrichie
      try {
        const response = await axios.get(`/api/rooms/${roomId}/details`);
        setRoomDetails(response.data);

        // Charger les charges seulement si la chambre a une réservation active
        if (response.data.reservation_id) {
          await loadCharges(response.data.reservation_id);
        }
        return;
      } catch (detailsError) {
        console.log('Nouvelle endpoint indisponible, utilisation de l\'approche fallback');

        // Fallback: utiliser les données de base et enrichir manuellement
        await loadRoomWithManualEnrichment();
      }
    } catch (error) {
      console.error('Erreur lors du chargement des détails:', error);
      setError(error);
      // En cas d'échec, utiliser les données initiales
      setRoomDetails(initialRoom);
    } finally {
      setLoading(false);
    }
  };

  const loadRoomWithManualEnrichment = async () => {
    try {
      // Récupérer les données de base de la chambre
      const roomResponse = await axios.get(`/api/rooms/${roomId}`);
      let enrichedRoom = roomResponse.data;

      // Si la chambre est occupée ou réservée, chercher la réservation active
      if (enrichedRoom.status === 'occupied' || enrichedRoom.status === 'reserved') {
        try {
          const reservationsResponse = await axios.get('/api/reservations');
          const activeReservation = reservationsResponse.data.find(
            r => r.room_id === parseInt(roomId) &&
            (r.status === 'checked_in' || r.status === 'confirmed') &&
            r.status !== 'cancelled' && r.status !== 'checked_out'
          );

          if (activeReservation) {
            // Enrichir les données de la chambre avec les informations de réservation
            enrichedRoom = {
              ...enrichedRoom,
              guest_name: activeReservation.guest_name,
              guest_email: activeReservation.guest_email,
              guest_phone: activeReservation.guest_phone,
              check_in: activeReservation.check_in,
              check_out: activeReservation.check_out,
              total_amount: activeReservation.total_amount,
              extra_charges: activeReservation.extra_charges,
              reservation_id: activeReservation.id,
              reservation_status: activeReservation.status
            };

            // Charger les charges additionnelles
            await loadCharges(activeReservation.id);
          }
        } catch (reservationError) {
          console.log('Erreur lors de la récupération des réservations:', reservationError);
        }
      }

      setRoomDetails(enrichedRoom);
    } catch (error) {
      console.error('Erreur dans le fallback:', error);
      setRoomDetails(initialRoom);
    }
  };

  const loadCharges = async (reservationId) => {
    try {
      const response = await axios.get(`/api/reservations/${reservationId}/charges`);
      setCharges(response.data || []);
    } catch (error) {
      console.error('Erreur lors du chargement des charges:', error);
      setCharges([]);
    }
  };

  useEffect(() => {
    if (roomId) {
      loadRoomDetailsWithFallback();
    }
  }, [roomId]);

  return { roomDetails, charges, loading, error, loadRoomDetailsWithFallback, loadCharges };
};

function RoomDetails({ room, onClose, onCheckIn, onCheckOut, onQuickReservation, onCancelReservation, onRoomUpdate, onChargeAdded, showAlert }) {
  const { roomDetails, charges, loading, error } = useRoomData(room.id, room);
  const [newCharge, setNewCharge] = useState({ description: '', amount: '' });
  const [showAddCharge, setShowAddCharge] = useState(false);

  // Utiliser les données enrichies du backend ou les données de base si pas encore chargées
  const currentRoom = roomDetails || room;


  // Gestion simplifiée des charges (KISS - Keep It Simple)
  const handleAddCharge = async (e) => {
    e.preventDefault();
    if (!newCharge.description || !newCharge.amount || !currentRoom.reservation_id) return;

    try {
      const response = await axios.post(`/api/reservations/${currentRoom.reservation_id}/charges`, {
        description: newCharge.description,
        amount: parseFloat(newCharge.amount)
      });

      setNewCharge({ description: '', amount: '' });
      setShowAddCharge(false);

      // UX optimisée : Mise à jour locale immédiate au lieu de reload
      if (onChargeAdded) {
        onChargeAdded(response.data);
      }
      if (showAlert) {
        showAlert('Charge ajoutée avec succès !', 'success');
      }

      // Fermer la modal après l'ajout de charge (cohérence UX)
      onClose();
    } catch (error) {
      console.error('Erreur lors de l\'ajout de la charge:', error);
      if (showAlert) {
        showAlert('Erreur lors de l\'ajout de la charge', 'error');
      }
    }
  };

  const totalCharges = charges.reduce((sum, charge) => sum + charge.amount, 0);
  const totalAmount = (currentRoom.total_amount || 0) + (currentRoom.extra_charges || 0) + totalCharges;

  const handleMaintenanceToggle = async () => {
    try {
      const newStatus = currentRoom.status === 'maintenance' ? 'available' : 'maintenance';
      const response = await axios.put(`/api/rooms/${currentRoom.id}/status`, { status: newStatus });

      if (response.status === 200) {
        // UX optimisée : Mise à jour locale immédiate
        const updatedRoom = { ...currentRoom, status: newStatus };
        if (onRoomUpdate) {
          onRoomUpdate(updatedRoom);
        }
        if (showAlert) {
          const message = newStatus === 'maintenance'
            ? 'Chambre mise en maintenance'
            : 'Maintenance terminée - chambre disponible';
          showAlert(message, 'success');
        }
        // Fermer la modal après l'opération
        onClose();
      }
    } catch (error) {
      console.error('Erreur lors du changement de statut:', error);
      if (showAlert) {
        showAlert('Erreur lors du changement de statut', 'error');
      }
    }
  };

  const handleCleaningToggle = async () => {
    try {
      const newStatus = currentRoom.status === 'cleaning' ? 'available' : 'cleaning';
      const response = await axios.put(`/api/rooms/${currentRoom.id}/status`, { status: newStatus });

      if (response.status === 200) {
        // UX optimisée : Mise à jour locale immédiate
        const updatedRoom = { ...currentRoom, status: newStatus };
        if (onRoomUpdate) {
          onRoomUpdate(updatedRoom);
        }
        if (showAlert) {
          const message = newStatus === 'cleaning'
            ? 'Chambre en cours de nettoyage'
            : 'Nettoyage terminé - chambre disponible';
          showAlert(message, 'success');
        }
        // Fermer la modal après l'opération
        onClose();
      }
    } catch (error) {
      console.error('Erreur lors du changement de statut:', error);
      if (showAlert) {
        showAlert('Erreur lors du changement de statut', 'error');
      }
    }
  };

  const handleCancelReservation = async () => {
    if (currentRoom.reservation_id && onCancelReservation) {
      await onCancelReservation(currentRoom.reservation_id);
      // La modal se ferme automatiquement via onCancelReservation dans App.js
    }
  };

  const getStatusBadge = (status) => {
    const statusMap = {
      available: { label: 'Libre', color: 'var(--available-green)', bg: 'var(--available-bg)' },
      occupied: { label: 'Occupée', color: 'var(--occupied-coral)', bg: 'var(--occupied-bg)' },
      reserved: { label: 'Réservée', color: 'var(--reserved-blue)', bg: 'var(--reserved-bg)' },
      maintenance: { label: 'Maintenance', color: 'var(--maintenance-amber)', bg: 'var(--maintenance-bg)' },
      cleaning: { label: 'Nettoyage', color: 'var(--cleaning-amber)', bg: 'var(--cleaning-bg)' }
    };

    const statusInfo = statusMap[status] || { label: status, color: 'var(--text-muted)', bg: 'var(--background)' };

    return (
      <span
        style={{
          background: statusInfo.bg,
          color: statusInfo.color,
          padding: '0.5rem 0.75rem',
          borderRadius: '8px',
          fontSize: '0.85rem',
          fontWeight: '600',
          border: `1px solid ${statusInfo.color}`
        }}
      >
        {statusInfo.label}
      </span>
    );
  };

  return (
    <div className="modal">
      <div className="modal-content">
        <div className="modal-header">
          <h2>Chambre {currentRoom.room_number}</h2>
          <button className="close-btn" onClick={onClose}>&times;</button>
        </div>

        <div className="modal-body">
          <div className="modal-section">
            <div className="room-info-grid">
              <div className="info-item">
                <span className="info-label">Type:</span>
                <span className="info-value">{currentRoom.type}</span>
              </div>
              <div className="info-item">
                <span className="info-label">Étage:</span>
                <span className="info-value">{currentRoom.floor}</span>
              </div>
              <div className="info-item">
                <span className="info-label">Prix/nuit:</span>
                <span className="info-value">€{currentRoom.price_per_night}</span>
              </div>
              <div className="info-item">
                <span className="info-label">Statut:</span>
                {getStatusBadge(currentRoom.status)}
              </div>
            </div>
          </div>

          {(currentRoom.status === 'occupied' || currentRoom.status === 'reserved') && (
            <div className="modal-section">
              <h3>Client Actuel</h3>
              {loading ? (
                <div className="loading-stats">
                  <div className="loading-spinner"></div>
                  <p>Chargement des données du client...</p>
                </div>
              ) : currentRoom.guest_name ? (
                <div className="guest-details-card">
                  <div className="guest-info-grid">
                    <div className="info-item">
                      <span className="info-label">Nom:</span>
                      <span className="info-value">{currentRoom.guest_name}</span>
                    </div>
                    {currentRoom.guest_email && (
                      <div className="info-item">
                        <span className="info-label">Email:</span>
                        <span className="info-value">{currentRoom.guest_email}</span>
                      </div>
                    )}
                    {currentRoom.guest_phone && (
                      <div className="info-item">
                        <span className="info-label">Téléphone:</span>
                        <span className="info-value">{currentRoom.guest_phone}</span>
                      </div>
                    )}
                    {currentRoom.check_in && (
                      <div className="info-item">
                        <span className="info-label">Arrivée:</span>
                        <span className="info-value">{new Date(currentRoom.check_in).toLocaleDateString()}</span>
                      </div>
                    )}
                    {currentRoom.check_out && (
                      <div className="info-item">
                        <span className="info-label">Départ:</span>
                        <span className="info-value">{new Date(currentRoom.check_out).toLocaleDateString()}</span>
                      </div>
                    )}
                    <div className="info-item">
                      <span className="info-label">Montant de base:</span>
                      <span className="info-value">€{((currentRoom.total_amount || 0) + (currentRoom.extra_charges || 0)).toFixed(2)}</span>
                    </div>
                    <div className="info-item">
                      <span className="info-label">Charges additionnelles:</span>
                      <span className="info-value">€{totalCharges.toFixed(2)}</span>
                    </div>
                  </div>

                  <div className="total-amount">
                    <span className="total-amount-label">Total à payer:</span>
                    <span className="total-amount-value">€{totalAmount.toFixed(2)}</span>
                  </div>
                </div>
              ) : (
                <p>Aucune information client disponible</p>
              )}
            </div>
          )}

              {charges.length > 0 && (
                <div className="modal-section">
                  <h4>Charges Additionnelles</h4>
                  <div className="charges-table-container">
                    <table className="table">
                      <thead>
                        <tr>
                          <th>Description</th>
                          <th>Montant</th>
                          <th>Date</th>
                        </tr>
                      </thead>
                      <tbody>
                        {charges.map(charge => (
                          <tr key={charge.id}>
                            <td>{charge.description}</td>
                            <td>€{charge.amount}</td>
                            <td>{new Date(charge.created_at).toLocaleDateString()}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}


              {!loading && showAddCharge && (
                <div className="modal-section">
                  <form onSubmit={handleAddCharge} className="charge-form">
                    <h4>Nouvelle Charge</h4>
                    <div className="form-group">
                      <label>Description</label>
                      <input
                        type="text"
                        value={newCharge.description}
                        onChange={(e) => setNewCharge(prev => ({ ...prev, description: e.target.value }))}
                        placeholder="Ex: Minibar, Room Service..."
                        required
                      />
                    </div>
                    <div className="form-group">
                      <label>Montant (€)</label>
                      <input
                        type="number"
                        step="0.01"
                        value={newCharge.amount}
                        onChange={(e) => setNewCharge(prev => ({ ...prev, amount: e.target.value }))}
                        placeholder="0.00"
                        required
                      />
                    </div>
                    <div className="form-actions">
                      <button type="submit" className="btn btn-primary btn-small">
                        Ajouter
                      </button>
                      <button
                        type="button"
                        className="btn btn-secondary btn-small"
                        onClick={() => setShowAddCharge(false)}
                      >
                        Annuler
                      </button>
                    </div>
                  </form>
                </div>
              )}

          {currentRoom.status === 'available' && (
            <div className="modal-section">
              <div style={{
                textAlign: 'center',
                padding: '3rem 2rem 1rem',
                color: 'var(--text-muted)',
                fontStyle: 'italic'
              }}>
                <div style={{ fontSize: '3rem', marginBottom: '1rem' }}>🏨</div>
                <p>Chambre disponible - Aucun client actuellement</p>
              </div>
            </div>
          )}

          {!loading && (
            <div className="modal-section">
              <div className="quick-actions">
                {/* Bouton Réservation Rapide - uniquement pour chambres disponibles */}
                {currentRoom.status === 'available' && onQuickReservation && (
                  <button
                    className="btn btn-primary"
                    onClick={() => {
                      onQuickReservation(currentRoom.id);
                      onClose();
                    }}
                    style={{
                      background: 'var(--primary-blue)',
                      border: 'none',
                      borderRadius: '12px',
                      padding: '0.875rem 2rem',
                      color: 'white',
                      fontSize: '1rem',
                      fontWeight: '600',
                      cursor: 'pointer',
                      transition: 'all 0.3s ease',
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: '0.5rem',
                      minHeight: '44px'
                    }}
                  >
                    ⚡ Réservation
                  </button>
                )}

                {/* Bouton Ajouter Charge - pour clients présents */}
                {currentRoom.guest_name && currentRoom.status !== 'maintenance' && (
                  <button
                    className="btn btn-primary"
                    onClick={() => setShowAddCharge(!showAddCharge)}
                  >
                    + Ajouter Charge
                  </button>
                )}

                {/* Boutons Check-in et Annuler - pour chambres réservées */}
                {currentRoom.status === 'reserved' && currentRoom.reservation_id && (
                  <>
                    <button
                      className="btn btn-success"
                      onClick={() => onCheckIn(currentRoom.reservation_id)}
                    >
                      Check-in
                    </button>
                    <button
                      className="btn btn-danger"
                      onClick={handleCancelReservation}
                    >
                      Annuler
                    </button>
                  </>
                )}

                {/* Bouton Check-out - pour chambres occupées */}
                {currentRoom.status === 'occupied' && currentRoom.reservation_id && (
                  <button
                    className="btn btn-danger"
                    onClick={() => onCheckOut(currentRoom.reservation_id)}
                  >
                    Check-out
                  </button>
                )}

                {/* Bouton Maintenance - disponible selon statut (pas pour chambres réservées) */}
                {currentRoom.status !== 'occupied' && currentRoom.status !== 'cleaning' && currentRoom.status !== 'reserved' && (
                  <button
                    className={`btn ${currentRoom.status === 'maintenance' ? 'btn-success' : 'btn-warning'}`}
                    onClick={handleMaintenanceToggle}
                    style={{
                      backgroundColor: currentRoom.status === 'maintenance' ? 'var(--available-green)' : 'var(--maintenance-amber)',
                      color: 'white',
                      border: 'none',
                      borderRadius: '8px',
                      padding: '0.75rem 1.5rem',
                      fontSize: '0.9rem',
                      fontWeight: '600',
                      minHeight: '44px'
                    }}
                  >
                    {currentRoom.status === 'maintenance' ? '✓ Réparé' : '🔧 Maintenance'}
                  </button>
                )}

                {/* Bouton Nettoyage - disponible selon statut (pas pour chambres réservées) */}
                {currentRoom.status !== 'occupied' && currentRoom.status !== 'maintenance' && currentRoom.status !== 'reserved' && (
                  <button
                    className={`btn ${currentRoom.status === 'cleaning' ? 'btn-success' : 'btn-info'}`}
                    onClick={handleCleaningToggle}
                    style={{
                      backgroundColor: currentRoom.status === 'cleaning' ? 'var(--available-green)' : 'var(--cleaning-amber)',
                      color: 'white',
                      border: 'none',
                      borderRadius: '8px',
                      padding: '0.75rem 1.5rem',
                      fontSize: '0.9rem',
                      fontWeight: '600',
                      minHeight: '44px'
                    }}
                  >
                    {currentRoom.status === 'cleaning' ? '✓ Nettoyé' : '🧹 Nettoyage'}
                  </button>
                )}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default RoomDetails;